import { rem } from 'polished'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import HighlightLabel from 'LuxKit/Label/HighlightLabel'
import CloseButton from 'components/Common/CloseButton/CloseButton'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import { INTL_DAY_LONG_MONTH } from 'constants/dateFormats'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import noop from 'lib/function/noop'

const Root = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${rem(24)};
  padding: ${rem(48)} ${rem(32)} ${rem(32)};
  background: linear-gradient(139deg, #304dab 0%, #4c82df 100%);
  color: ${props => props.theme.palette.neutral.surface.staticWhite};
  border-radius: ${props => props.theme.borderRadius.M};
  box-shadow: ${props => props.theme.shadow.bottom.medium};
`

const PositionedCloseButton = styled(CloseButton)`
  position: absolute;
  top: ${rem(12)};
  inset-inline-end: ${rem(12)};
  color: ${props => props.theme.palette.neutral.surface.staticWhite};
`

const WhiteText = styled(BodyText)`
  margin: 0;
  color: inherit;
  line-height: ${rem(20)};
`

const Highlight = styled.span`
  color: #00f1bd;
`

const PremiumMembership = styled.i`
  && {
    font-style: italic;
  }
`

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(8)};
  margin-top: ${rem(8)};
`

// A solid white button with dark text on the blue gradient. The base button derives every colour
// from the `--color-primary-*` custom properties, so we override those — with enough specificity
// (`&&.kind-primary`) to beat the base `.kind-primary.variant-default` selector.
const ContinueButton = styled(TextButton)`
  &&.kind-primary {
    --color-primary-bg: ${props => props.theme.palette.neutral.surface.staticWhite};
    --color-primary-fg: ${props => props.theme.palette.neutral.surface.normalContrast};
    --color-primary-hover: ${props => props.theme.palette.neutral.surface.staticWhite};
    --color-primary-active: ${props => props.theme.palette.neutral.surface.staticWhite};
  }
  min-width: ${rem(220)};
`

const LearnMoreLink = styled(TextLink)`
  color: inherit;
`

interface Props {
  /** Dismiss the modal. Fired by the close button and "Continue browsing". */
  onClose?: () => void
  /** Where "Learn more about LuxPlus+" links to. */
  learnMoreTo?: string
  /**
   * Whether to badge the preview as a welcome gift. Only true for a customer who just registered —
   * an existing customer picked up by a campaign is not being welcomed.
   */
  showWelcomeGiftLabel?: boolean
  /** When the preview ends (ISO); the body copy states it ("…until 1 September."). */
  previewEndDate: string
}

/**
 * Presentational "you've unlocked a free preview" celebration shown after a customer is enrolled
 * into the LuxPlus+ Free Preview. Kept free of Redux/modal wiring so it can be rendered in
 * Storybook and tests; the connected wrapper is LuxPlusFreePreviewUnlockedModal. Figma UX-4738.
 */
function LuxPlusFreePreviewUnlockedModalContent({ onClose = noop, learnMoreTo, showWelcomeGiftLabel = true, previewEndDate }: Props) {
  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()

  return (
    <Root>
      <PositionedCloseButton variant="ghost" size="small" onClick={onClose} />
      {showWelcomeGiftLabel && <HighlightLabel>
        {intl.formatMessage({
          id: 'VGDBlsuJZP8Q4oKEGSY9',
          defaultMessage: 'Your welcome gift',
          description: 'Label badge shown at the top of the Free Preview unlocked celebration modal',
        })}
      </HighlightLabel>}
      <LuxPlusLogo colour="inherit" height={rem(20)} />
      <Heading variant="heading3" colour="static-white">
        <FormattedMessage
          id="otAvG7KBqrX2SfOPfJg0"
          defaultMessage="You’ve unlocked a <highlight>{duration}-day free preview</highlight> of our <italic>premium membership</italic>"
          description="Heading on the Free Preview unlocked celebration modal shown after enrolment"
          values={{
            duration: LUXURY_PLUS.FREE_PREVIEW_DURATION,
            highlight: (chunks) => <Highlight>{chunks}</Highlight>,
            italic: (chunks) => <PremiumMembership>{chunks}</PremiumMembership>,
          }}
        />
      </Heading>
      <WhiteText as="p" variant="medium">
        <FormattedMessage
          id="MFvg9B0nHkh8jB4Sw0h0"
          defaultMessage="You can now view hidden member-only offers until {endDate}."
          description="Body copy on the Free Preview unlocked modal, stating when the preview ends"
          values={{ endDate: intlDateFormatter(previewEndDate, INTL_DAY_LONG_MONTH) }}
        />
      </WhiteText>
      <Actions>
        <ContinueButton kind="primary" size="large" onClick={onClose}>
          {intl.formatMessage({
            id: 'FWK17qOCOkjw15JxC7CP',
            defaultMessage: 'Continue browsing',
            description: 'Primary button on the Free Preview unlocked modal that dismisses it',
          })}
        </ContinueButton>
        <LearnMoreLink size="medium" weight="regular" underline to={learnMoreTo} onClick={onClose}>
          {intl.formatMessage({
            id: 'taCAWw-B30w4NTpsYkQA',
            defaultMessage: 'Learn more about {programName}',
            description: 'Link on the Free Preview unlocked modal to the membership landing page',
          }, { programName: LUXURY_PLUS.PROGRAM_NAME })}
        </LearnMoreLink>
      </Actions>
    </Root>
  )
}

export default LuxPlusFreePreviewUnlockedModalContent
